import { Result } from 'better-result';
import type { TryPromiseContext } from 'better-result';
import { InfrastructureError, UnauthorizedError, ValidationError } from '@finnest/core/result';
import { CustomerId } from '@finnest/power-admin-contracts';
import { Result as SchemaResult, Schema } from 'effect';
import { z } from 'zod';

export type FetchLike = (
  input: string,
  init: RequestInit & { readonly signal: AbortSignal },
) => Promise<Response>;

export type OperatorHttp = {
  readonly fetcher: FetchLike;
  readonly signal: AbortSignal;
};

const OPERATOR_HTTP_TIMEOUT_MS = 15_000;

// Callers invoke `operatorHttp.fetcher(...)` as a method; browsers reject the native `fetch`
// with any receiver other than the global ("Illegal invocation"), so it is never stored bare.
export const browserHttp = (): OperatorHttp => ({
  fetcher: (input, init) => fetch(input, init),
  signal: AbortSignal.timeout(OPERATOR_HTTP_TIMEOUT_MS),
});

// `context` is better-result's own tryPromise attempt context (external shape):
// its signal is present only when the tryPromise config supplied one.
export const boundFetcherSignal = (
  operatorHttp: OperatorHttp,
  context: Pick<TryPromiseContext, 'signal'>,
): AbortSignal => context.signal ?? operatorHttp.signal;

const VitePowerAdminEnv = z.object({
  VITE_POWER_ADMIN_API_BASE_URL: z.union([z.url(), z.literal('')]),
});

const apiBaseFromLocation = (hostname: string, protocol: string, origin: string): string => {
  if (hostname.startsWith('admin.')) {
    return `${protocol}//api.${hostname.slice('admin.'.length)}`;
  }
  return origin;
};

export const resolvePowerAdminApiBaseUrl = (): string => {
  const raw: z.input<typeof VitePowerAdminEnv> = {
    VITE_POWER_ADMIN_API_BASE_URL: import.meta.env.VITE_POWER_ADMIN_API_BASE_URL ?? '',
  };
  const env = VitePowerAdminEnv.parse(raw);
  if (env.VITE_POWER_ADMIN_API_BASE_URL !== '') return env.VITE_POWER_ADMIN_API_BASE_URL;
  return apiBaseFromLocation(
    window.location.hostname,
    window.location.protocol,
    window.location.origin,
  );
};

export const powerAdminApiPath = (path: `/${string}`): string =>
  `${resolvePowerAdminApiBaseUrl()}${path}`;

const operatorSessionPath = '/api/auth/get-session' as const;

const EnrichedOperatorSession = Schema.Struct({
  customer: Schema.Struct({
    customerId: CustomerId,
  }),
}).annotate({
  title: 'Enriched operator session customer',
  description: 'Power IAM get-session body after grant enrichment; carries customer.customerId.',
});
type EnrichedOperatorSession = typeof EnrichedOperatorSession.Type;
const decodeOperatorSession = Schema.decodeResult(Schema.fromJsonString(EnrichedOperatorSession));

const operatorSessionFromText = (
  text: string,
): Result<EnrichedOperatorSession, ValidationError> => {
  const decoded = decodeOperatorSession(text);
  return SchemaResult.isSuccess(decoded)
    ? Result.ok(decoded.success)
    : Result.err(
        new ValidationError({
          field: 'json',
          message: decoded.failure.message,
          cause: decoded.failure,
        }),
      );
};

const sessionUnauthorized = (message: string): UnauthorizedError =>
  new UnauthorizedError({ reason: 'missing_token', message });

const sessionTransport = (cause: unknown): InfrastructureError => {
  const error = cause instanceof Error ? cause : new Error('operator session request failed');
  return new InfrastructureError({
    system: 'http',
    message: error.message,
    cause: error,
    publicMessage: 'Não foi possível ler a sessão do operador.',
  });
};

const readSessionText = (
  operatorHttp: OperatorHttp,
): Promise<Result<string, InfrastructureError | UnauthorizedError>> =>
  Result.gen(async function* () {
    const fetched = yield* Result.await(
      Result.tryPromise(
        {
          try: async (context) => {
            const response = await operatorHttp.fetcher(powerAdminApiPath(operatorSessionPath), {
              credentials: 'include',
              headers: { accept: 'application/json' },
              signal: boundFetcherSignal(operatorHttp, context),
            });
            return { ok: response.ok, text: await response.text() };
          },
          catch: sessionTransport,
        },
        { signal: operatorHttp.signal },
      ),
    );
    if (!fetched.ok) {
      return Result.err(sessionUnauthorized('operator session is unauthenticated'));
    }
    return Result.ok(fetched.text);
  });

export const fetchOperatorCustomerId = (
  operatorHttp: OperatorHttp,
): Promise<Result<CustomerId, InfrastructureError | UnauthorizedError | ValidationError>> =>
  Result.gen(async function* () {
    const text = yield* Result.await(readSessionText(operatorHttp));
    const session = yield* operatorSessionFromText(text);
    return Result.ok(session.customer.customerId);
  });
